import React, { useContext, useState } from "react";

import { MoreVert } from "@mui/icons-material";
import {
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import { DateTime } from "luxon";
import { Clipboard, ExternalLink } from "react-feather";
import { Link } from "react-router-dom";
import mainStyles from "styles";

import { Inspection } from "@safetyculture/s12-apis-connect-web/s12/inspections/v1/inspection_pb";
import { Template } from "@safetyculture/s12-apis-connect-web/s12/templates/v1/template_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { Region } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { convertTimestampToDate } from "../../../../helpers/time";
import ConfirmDialog from "components/shared/confirm-dialog";
import CopyTemplateDialog from "components/user-display/tabs/components/copy-template-dialog";
import RestoreTemplateVersion from "components/user-display/tabs/components/restore-template-version";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";

interface UserTemplatesAndInspectionsRecordProps {
  row: Template | Inspection;
  key: number;
  documentType: string;
  selected: boolean;
  setSelected: (newSelected: boolean) => void;
  restoreTemplateVersion: (templateId: string, targetUserId: string) => void;
  copyTemplate: (
    templateId: string,
    targetUserId: string,
    targetOrgId: string,
    targetRegion: Region
  ) => void;
}

const UserTemplatesAndInspectionsRecord = ({
  row,
  key,
  documentType,
  selected,
  setSelected,
  restoreTemplateVersion,
  copyTemplate,
}: UserTemplatesAndInspectionsRecordProps) => {
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [openRestoreTemplateVersionDialog, setRestoreTemplateVersionDialog] =
    useState(false);
  const [openCopyTemplateDialog, setCopyTemplateDialog] = useState(false);
  const handleActionMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setActionMenuAnchor(event.currentTarget);
  };
  const handleRestoreTemplateVersionDialog = () => {
    setActionMenuAnchor(null);
    setRestoreTemplateVersionDialog(true);
  };
  const handleCopyTemplateDialog = () => {
    setActionMenuAnchor(null);
    setCopyTemplateDialog(true);
  };

  const [targetUserId, setTargetUserId] = useState("");
  const [targetOrgId, setTargetOrgId] = useState("");
  const [targetRegion, setTargetRegion] = useState<Region>(Region.UNSPECIFIED);
  const [
    disableRestoreTemplateConfirmBtn,
    setDisableRestoreTemplateConfirmBtn,
  ] = useState(true);
  const [disableCopyTemplateConfirmBtn, setDisableCopyTemplateConfirmBtn] =
    useState(true);
  const { copyToClipboard } = useContext(SnackbarContext);
  const classes = mainStyles();
  const theme = useTheme();

  return (
    <React.Fragment>
      <TableRow key={`${key}-${row.id}`}>
        <TableCell>
          <Checkbox
            checked={selected}
            onChange={(e) => setSelected(e.target.checked)}
            inputProps={
              {
                "data-testid": `user-document-${documentType}-selected`,
                // eslint-disable-next-line
              } as any
            }
          />
        </TableCell>

        <TableCell>
          <Typography
            variant="body2"
            display="inline"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {documentType == "template"
              ? (row as Template).name
              : (row as Inspection).title}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            display="inline"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {row.createdAt
              ? DateTime.fromJSDate(
                  convertTimestampToDate(row.createdAt)
                ).toFormat("dd MMM yyyy, hh:mm a")
              : "-"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            display="inline"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {row.modifiedAt
              ? DateTime.fromJSDate(
                  convertTimestampToDate(row.modifiedAt)
                ).toFormat("dd MMM yyyy, hh:mm a")
              : "-"}
          </Typography>
        </TableCell>
        {documentType == "template" && (
          <TableCell align="center">
            <Typography
              variant="body2"
              display="inline"
              style={{
                whiteSpace: "normal",
                wordBreak: "break-all",
              }}
            >
              {(row as Template).archived ? "Y" : "N"}
            </Typography>
          </TableCell>
        )}
        <TableCell>
          <Typography
            variant="body2"
            display="inline"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-all",
            }}
          >
            {row.id} &nbsp;
            <Clipboard
              color={theme.palette.text.primary}
              size={"1rem"}
              className={clsx(classes.positionIcon, classes.clickable)}
              onClick={() => copyToClipboard(row.id)}
            />
            &nbsp;
            <Link
              to={{
                pathname: `/search/doc/${row.id}`,
              }}
              target="_blank"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <ExternalLink
                color={theme.palette.text.primary}
                size={"1rem"}
                className={clsx(classes.positionIcon, classes.clickable)}
              />
            </Link>
          </Typography>
        </TableCell>
        {documentType == "inspection" && (
          <>
            <TableCell>
              <Typography
                variant="body2"
                display="inline"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
              >
                {(row as Inspection).templateId}
                &nbsp;
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  className={clsx(classes.positionIcon, classes.clickable)}
                  onClick={() =>
                    copyToClipboard((row as Inspection).templateId)
                  }
                />
                &nbsp;
                <Link
                  to={{
                    pathname: `/search/doc/${(row as Inspection).templateId}`,
                  }}
                  target="_blank"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <ExternalLink
                    color={theme.palette.text.primary}
                    size={"1rem"}
                    className={clsx(classes.positionIcon, classes.clickable)}
                  />
                </Link>
              </Typography>
            </TableCell>
          </>
        )}
        <TableCell>
          <IconButton
            onClick={handleActionMenuClick}
            data-testid={`show-more-${documentType}-actions-button`}
          >
            <MoreVert />
          </IconButton>
        </TableCell>
        <Menu
          open={Boolean(actionMenuAnchor)}
          anchorEl={actionMenuAnchor}
          onClose={() => setActionMenuAnchor(null)}
        >
          {documentType == "inspection" ? (
            <MenuItem
              sx={{ color: "error.main" }}
              onClick={handleRestoreTemplateVersionDialog}
              disabled={
                permissions.isPermissionsLoading ||
                !hasPermissions([ActionType.RESTORE_TEMPLATE_VERSION])
              }
              data-testid={"restore-template-version-btn"}
            >
              Restore Template Version From Inspection
            </MenuItem>
          ) : (
            <MenuItem
              sx={{ color: "error.main" }}
              onClick={handleCopyTemplateDialog}
              disabled={
                permissions.isPermissionsLoading ||
                !hasPermissions([ActionType.COPY_TEMPLATE])
              }
              data-testid={"copy-template-btn"}
            >
              Copy Template to Another User
            </MenuItem>
          )}
        </Menu>
      </TableRow>
      <ConfirmDialog
        title={"Restore Template Version from Inspection"}
        open={openRestoreTemplateVersionDialog}
        setOpen={setRestoreTemplateVersionDialog}
        onConfirm={() => {
          return restoreTemplateVersion(row.id, targetUserId);
        }}
        onCancel={() => {
          setTargetUserId("");
        }}
        disabled={disableRestoreTemplateConfirmBtn}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Restore"
      >
        <RestoreTemplateVersion
          handleTargetUserId={(targetUserId: string) =>
            setTargetUserId(targetUserId)
          }
          handleRestoreTemplateBtn={(isDisabled: boolean) => {
            setDisableRestoreTemplateConfirmBtn(isDisabled);
          }}
        />
      </ConfirmDialog>
      <ConfirmDialog
        title={"Copy Template to Another User"}
        open={openCopyTemplateDialog}
        setOpen={setCopyTemplateDialog}
        onConfirm={() => {
          return copyTemplate(row.id, targetUserId, targetOrgId, targetRegion);
        }}
        onCancel={() => {
          setTargetUserId("");
        }}
        disabled={disableCopyTemplateConfirmBtn}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Copy"
      >
        <CopyTemplateDialog
          handleTargetUserId={(targetUserId: string) =>
            setTargetUserId(targetUserId)
          }
          handleTargetOrgId={(targetOrgId: string) => {
            setTargetOrgId(targetOrgId);
          }}
          handleTargetRegion={(targetRegion: Region) => {
            setTargetRegion(targetRegion);
          }}
          handleCopyTemplateDialogBtn={setDisableCopyTemplateConfirmBtn}
          templateId={row.id}
        />
      </ConfirmDialog>
    </React.Fragment>
  );
};

export default UserTemplatesAndInspectionsRecord;
